<script>
	import CaseStudy from '$lib/Components/CaseStudy/CaseStudy.svelte';
	import DropdownList from '$lib/Components/Layout/DropdownList/DropdownList.svelte';
	import Grid from '$lib/Components/Layout/Grid/Grid.svelte';
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import Awards from '$lib/PageSections/Awards/Awards.svelte';
	import GetAFreeEstimate from '$lib/PageSections/GetAFreeEstimate/GetAFreeEstimate.svelte';

	import soundproofingWindowImage from './images/CS-drafty-double-pane-windows-03.jpg';
	import energyEfficiencyWindowImage from './images/CS-window-insert-options-03.png';
	import windowImage from './images/IW-Window-Keyword-HUB-image-01.jpg';
	import windowTypesImage from './images/IW-Window-Keyword-HUB-image-2C.png';
	import windowComparisonImage from './images/IW-Window-Keyword-HUB-image-2D.png';
</script>

<Section backgroundColor={'white'}>
	<h2>Will Indow Inserts Work with My Windows?</h2>
	<p>
		Through our window guides, we'll help assess and address any window,
		comfort, or efficiency issue you have. Consider this your window research
		headquarters.
	</p>
	<p>
		Getting window inserts can help solve a variety of issues: Do you have
		drafty, cold windows? Do you want a more energy efficient home? Want to
		block light or UV rays coming through your windows? Maybe you live in an
		historic area and are restricted in your window options due to local
		regulations. Whatever the reasons, these window guides will help walk you
		through the options given a variety of situations.
	</p>
	<p>
		Use our window guides to learn how a window insert can help you depending on
		your location, building type, window shape & type, and the main problem your
		facing.
	</p>
	<Image src={windowImage} alt="One window option from Indow window guides." />
</Section>

<Section backgroundColor={'white'}>
	<h2>Find your perfect window attachment by:</h2>
	<hr class="hidden md:block" />
	<Grid columns={2} rows={3} center>
		<Grid subgrid vertical>
			<CaseStudy
				src={windowComparisonImage}
				alt="Indow window guide - comparing windows."
				title="Comparing Windows"
				subgrid
			>
				{#snippet descriptionSlot()}
					<div>
						<p>
							Find the right window attachment for your home via these
							comprehensive comparison articles.
						</p>
						<DropdownList title="Compare Articles" centerTitle>
							<li class="dropdown-list-item">
								<a href="/resources/storm-windows-guide/replacement"
									>Indow vs. Replacement Windows</a
								>
							</li>
							<li class="dropdown-list-item">
								<a href="/resources/storm-windows-guide/traditional"
									>Indow vs. Exterior Storm Windows</a
								>
							</li>
							<li class="dropdown-list-item">
								<a href="/resources/blog/magnetic-storm-windows-pros-and-cons"
									>Indow vs. Magnetic Storm Windows</a
								>
							</li>
						</DropdownList>
					</div>
				{/snippet}
			</CaseStudy>
		</Grid>
		<Grid subgrid vertical>
			<CaseStudy
				src={windowTypesImage}
				alt="Indow window guide - compare window type."
				title="Window Types"
				subgrid
			>
				{#snippet descriptionSlot()}
					<div>
						<p>
							Single-pane, double-pane, special shape, there's a window insert
							for every kind of window.
						</p>
						<!--<p>[[This is a placeholder for the drop down menu.]]</p>-->
						<DropdownList title="Window Type Articles" centerTitle>
							<li class="dropdown-list-item">
								<a href="/solutions/odd-shaped-window-inserts"
									>Window Inserts in Special Shaped Windows</a
								>
							</li>
							<li class="dropdown-list-item">
								<a href="/resources/window-guides/skylight-window-inserts"
									>Window Inserts in Skylights</a
								>
							</li>
							<li class="dropdown-list-item">
								<a href="/about-indow/how-to-order/mullions"
									>Window Inserts in Large Windows</a
								>
							</li>
						</DropdownList>
					</div>
				{/snippet}
			</CaseStudy>
		</Grid>
	</Grid>
	<hr />
</Section>

<Section backgroundColor={'white'}>
	<h2>Research by Topic</h2>
	<p>
		What is the main reason you want to upgrade your windows? Are they a step in
		making your home more energy efficient? Do you want to block outside noise?
		These window guides were put together by on a particular topic, solving a
		specific solution for a space.
	</p>
	<Image
		src={energyEfficiencyWindowImage}
		alt="Indow window guide - research by topic."
	/>
	<h3>Energy Efficient</h3>
	<p>
		Air sealing and insulating your windows is a part of making an energy
		efficient home.
	</p>
	<h3>Soundproofing</h3>
	<p>
		You can't get 100% soundproof windows, but you can get really close. Our <a
			href="/resources/soundproofing-windows">soundproof window guides</a
		>
		page has everything you need to know before you undertake your window soundproofing
		project: product data, case studies, other soundproofing products and ways to
		soundproof different rooms in your home, including your
		<a href="/resources/blog/soundproof-home-office">home office</a>. Read our
		<a href="/resources/stc-rating">“What Is STC Rating?”</a> article to find out
		more about how soundproofing products are measured.
	</p>

	<Grid columns={2} rows={2}>
		<DropdownList title={'Home Soundproofing How Tos'}>
			<li class="dropdown-list-item">
				<a href="/resources/blog/soundproof-apartment">Apartment</a>
			</li>
			<li class="dropdown-list-item">
				<a href="/resources/home-soundproofing">House</a>
			</li>
			<li class="dropdown-list-item">
				<a href="/resources/blog/soundproof-home-office">Home Office</a>
			</li>
			<li class="dropdown-list-item">
				<a href="/resources/soundproof-room-music">Music Room</a>
			</li>
			<li class="dropdown-list-item">
				<a href="/resources/blog/how-to-soundproof-window-air-conditioner"
					>Window with an AC Unit</a
				>
			</li>
		</DropdownList>

		<DropdownList title={'Other Soundproofing Products'}>
			<li class="dropdown-list-item">
				<a href="/resources/blog/soundproof-curtains">Soundproofing Curtains</a>
			</li>
			<li class="dropdown-list-item">
				<a href="/resources/blog/most-effective-soundproofing-materials"
					>Most Effective Soundproofing Materials</a
				>
			</li>
		</DropdownList>

		<DropdownList title={'Legal References'}>
			<li class="dropdown-list-item">
				<a href="/resources/blog/how-to-file-noise-complaint-online"
					>How to File Noise Complaint</a
				>
			</li>
		</DropdownList>

		<DropdownList title={'Commercial Soundproofing How Tos'}>
			<li class="dropdown-list-item">
				<a href="/resources/tips-soundproofing-commercial-spaces"
					>Office Soundproofing Tips</a
				>
			</li>
			<li class="dropdown-list-item">
				<a href="/resources/blog/soundproof-home-office"
					>Home Office Soundproofing Tips</a
				>
			</li>
			<li class="dropdown-list-item">
				<a href="/commercial-storm-windows/hotel-soundproofing"
					>Hotel Soundproofing</a
				>
			</li>
		</DropdownList>
	</Grid>

	<Image
		src={soundproofingWindowImage}
		alt="Closeup of Indow window insert - window guide."
	/>
	<p style="font-style: italic;">
		These window guides were put together based on our own research and <a
			href="/performance">third party research</a
		>. The third party testing was for efficiency and sound resistance of Indow
		window inserts. The US Department of Energy and Portland State University's
		Green Building Research Lab tested Indow window inserts against multiple
		functions and capabilities depending on the grade type.
	</p>
	<p>
		Indow is a light-weight insert that presses into your existing window frame.
		It is held in place with compression tubing, like the kind in your
		refrigerator. Indow window inserts comes in seven different grade types that
		work with different windows, spaces, and issues. No matter what type of
		window you have, or what issue you are dealing with, we've done the research
		and have created the window guide for it. If you don't see an answer to your
		question in our window guides, please contact us. We'd love to answer your
		window question! Window soundproofing? Energy efficiency? We can discuss it
		all.
	</p>
</Section>

<Awards />
<GetAFreeEstimate />
